import type {
  APIKey,
  ScienceLLM,
  UsageRecord,
  UsageSummaryItem,
  AdaptedAPIKey,
  AdaptedModel,
  AdaptedUsageLog,
  AdaptedUsageChart,
} from "./types";
import { formatSTAmount } from "./utils";
import { translations } from "./translations";
import type { Language } from "./translations";

// =============================================================================
// Helpers
// =============================================================================

function formatRelativeTime(isoString: string | undefined, language: Language = "zh"): string {
  if (!isoString) return translations["time.never"][language];
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return translations["time.justNow"][language];
  if (diffMin < 60) return translations["time.minAgo"][language].replace("{n}", String(diffMin));
  if (diffHr < 24) return translations["time.hrAgo"][language].replace("{n}", String(diffHr));
  if (diffDay < 7) return translations["time.dayAgo"][language].replace("{n}", String(diffDay));
  return date.toLocaleDateString();
}

function formatTimestamp(isoString: string, language: Language = "zh"): string {
  const date = new Date(isoString);
  const locale = language === "zh" ? "zh-CN" : "en-US";
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function formatDate(isoString: string): string {
  return isoString.split("T")[0];
}

// =============================================================================
// API Key adapter
// =============================================================================

export function adaptApiKey(key: APIKey, language: Language = "zh"): AdaptedAPIKey {
  // Revoked takes precedence over expired: the backend stores both fields
  // independently and ValidateByKey rejects either at proxy time, so this
  // derived label is purely cosmetic — revocation is the more useful signal
  // when both flags are set.
  let status: AdaptedAPIKey["status"] = "active";
  if (key.revoked_at) {
    status = "revoked";
  } else if (key.expires_at && new Date(key.expires_at).getTime() <= Date.now()) {
    status = "expired";
  }
  return {
    id: key.id,
    name: key.name,
    nameEn: key.name,
    key: key.key_prefix,
    created: formatDate(key.created_at),
    lastUsed: formatRelativeTime(key.last_used_at, language),
    monthlyUsage: 0, // backend list doesn't include per-key usage
    limit: null,
    status,
    isLegacy: key.has_ciphertext === false,
  };
}

// =============================================================================
// Model adapter — the public listing returns ScienceLLM (product) rows. The
// product carries its own user-facing sell price (ST per Ktok), surfaced here
// so the console detail view can show what each model costs to call.
// =============================================================================

export function adaptModel(sl: ScienceLLM): AdaptedModel {
  return {
    id: sl.model_key,
    name: sl.name,
    provider: "ScienceLLM",
    providerShort: "SL",
    tier: "Standard",
    contextLength: sl.context_window,
    inputPrice: sl.input_st_per_ktok ?? 0,
    outputPrice: sl.output_st_per_ktok ?? 0,
    cacheWritePrice: sl.cache_write_st_per_ktok ?? 0,
    cacheReadPrice: sl.cache_read_st_per_ktok ?? 0,
    rpmLimit: sl.rpm_limit ?? null,
    tpmLimit: sl.tpm_limit ?? null,
    tags: [],
    description: sl.description || "",
    descriptionEn: sl.description || "",
    capabilities: [],
    status: sl.is_active === false ? "unavailable" : "available",
    isNew: false,
    isFeatured: false,
    protocol: sl.protocol,
  };
}

// =============================================================================
// Usage Record adapter
// =============================================================================

export function adaptUsageRecord(
  record: UsageRecord,
  apiKeyMap: Record<string, string> = {},
  language: Language = "zh"
): AdaptedUsageLog {
  return {
    id: record.id,
    requestId: record.request_id || record.id,
    model: record.science_llm_key || record.model_key || "—",
    upstreamModel: record.model_key && record.model_key !== record.science_llm_key ? record.model_key : undefined,
    provider: record.provider,
    type: "llm",
    status:
      record.status === "success"
        ? "success"
        : record.status === "pending"
          ? "pending"
          : "failed",
    timestamp: formatTimestamp(record.started_at, language),
    tokens: record.total_tokens,
    inputTokens: record.input_tokens,
    outputTokens: record.output_tokens,
    cacheReadTokens: record.cache_read_tokens,
    cacheWriteTokens: record.cache_write_tokens,
    latency: record.latency_ms || 0,
    cost: formatSTAmount(record.actual_amount),
    keyName: apiKeyMap[record.api_key_id || ""] || "—",
    errorMessage: record.status !== "success" ? (record.error_code || undefined) : undefined,
    errorDetail: record.error_detail || undefined,
    userId: record.user_id,
    teamId: record.team_id,
    fundingSource: record.funding_source,
    listAmount: record.list_amount,
    discountRate: record.discount_rate,
    voucherAmount: record.voucher_amount,
    cashAmount: record.cash_amount,
    creditAmount: record.credit_amount,
  };
}

// =============================================================================
// Usage Chart adapter
// =============================================================================

export function adaptUsageChart(
  items: UsageSummaryItem[]
): AdaptedUsageChart[] {
  return items.map((item) => ({
    date: item.group_key,
    inputTokens: item.input_tokens,
    outputTokens: item.output_tokens,
    cacheReadTokens: item.cache_read_tokens,
    cacheWriteTokens: item.cache_write_tokens,
  }));
}
